import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useStore, put, remove, generateId } from '../hooks/useDB';
import PageLoader from '../components/PageLoader';
import {
  Plus, Trash2, Edit2, X, PhoneCall, MapPin, Calendar, Inbox, Search, Loader2,
  Navigation, ChevronDown, Radio, AlertTriangle, DownloadCloud, ListTree, Clock,
} from 'lucide-react';
import { toast } from '../utils/toast';
import { confirm } from '../utils/confirm';
import {
  OFFSITE_ITINERARY, OFFSITE_BLOCK_COUNT, OFFSITE_SLOT_COUNT,
} from '../data/offsiteItinerary';
import {
  toMinutes, timeRangeLabel, durationLabel, formatTime, toISODate, longDateLabel,
  shortDateLabel, sortItems, statusOf, nextUpId, mapsHref, agendaOf, groupByDay,
} from '../utils/schedule';

const TYPES = ['session', 'meal', 'break', 'activity', 'travel', 'check-in', 'check-out', 'meeting'];
const typeColor = {
  session: 'sh-badge-info',
  meal: 'sh-badge-warning',
  break: 'sh-badge-outline',
  activity: 'sh-badge-success',
  travel: 'sh-badge-danger',
  'check-in': 'sh-badge-success',
  'check-out': 'sh-badge-danger',
  meeting: 'sh-badge-info',
};

// How often the "happening now" / "up next" markers re-evaluate. A minute is
// plenty — the badges only ever change on a block boundary.
const CLOCK_TICK_MS = 30000;

const EMPTY_FORM = {
  day: '', date: '', startTime: '', endTime: '', endsNextDay: false, openEnded: false,
  title: '', type: 'session', speaker: '', location: '', address: '', mapsUrl: '',
  conferenceRoom: '', pocName: '', pocPhone: '', description: '', internalNote: '',
  dayTheme: '', agenda: [], published: false,
};

// ── Sub-agenda editor ────────────────────────────────────────────────────────
// A long block (the Saturday quarterly meet) is really a container for a run of
// short timed slots. They live on the parent row rather than as sibling rows
// because they have no venue, POC or publish state of their own — one write
// updates the whole block.
function AgendaEditor({ slots, onChange }) {
  const update = (index, key, value) => {
    onChange(slots.map((slot, i) => (i === index ? { ...slot, [key]: value } : slot)));
  };
  const add = () => onChange([...slots, { id: generateId(), startTime: '', endTime: '', title: '', owner: '', note: '' }]);
  const removeAt = (index) => onChange(slots.filter((_, i) => i !== index));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
          <ListTree size={13} className="text-slate-400" />
          Sub-agenda ({slots.length})
        </label>
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-slate-950 border border-slate-200 hover:border-slate-400 rounded-lg px-2 py-1 transition"
        >
          <Plus size={12} /> Add slot
        </button>
      </div>

      {slots.length === 0 ? (
        <p className="text-xs text-slate-400 italic border border-dashed border-slate-200 rounded-lg px-3 py-3 text-center">
          No sub-slots. Add them to break a long block into a timed run of items.
        </p>
      ) : (
        <div className="space-y-2">
          {slots.map((slot, index) => (
            <div key={slot.id || index} className="border border-slate-200 rounded-lg p-2.5 bg-slate-50/60 space-y-2">
              <div className="flex items-start gap-2">
                <input
                  type="time"
                  value={slot.startTime || ''}
                  onChange={(e) => update(index, 'startTime', e.target.value)}
                  aria-label={`Sub-slot ${index + 1} start time`}
                  className="w-[7.5rem] bg-white border border-slate-200 rounded-md px-2 py-1 text-xs outline-none focus:border-slate-800"
                />
                <input
                  type="time"
                  value={slot.endTime || ''}
                  onChange={(e) => update(index, 'endTime', e.target.value)}
                  aria-label={`Sub-slot ${index + 1} end time`}
                  className="w-[7.5rem] bg-white border border-slate-200 rounded-md px-2 py-1 text-xs outline-none focus:border-slate-800"
                />
                <button
                  type="button"
                  onClick={() => removeAt(index)}
                  title="Remove this slot"
                  aria-label={`Remove sub-slot ${index + 1}`}
                  className="ml-auto p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition shrink-0"
                >
                  <Trash2 size={13} />
                </button>
              </div>
              <input
                value={slot.title || ''}
                onChange={(e) => update(index, 'title', e.target.value)}
                placeholder="Slot title, e.g. Product strategy"
                aria-label={`Sub-slot ${index + 1} title`}
                className="w-full bg-white border border-slate-200 rounded-md px-2 py-1 text-xs outline-none focus:border-slate-800"
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  value={slot.owner || ''}
                  onChange={(e) => update(index, 'owner', e.target.value)}
                  placeholder="Owner, e.g. Venkat"
                  aria-label={`Sub-slot ${index + 1} owner`}
                  className="w-full bg-white border border-slate-200 rounded-md px-2 py-1 text-xs outline-none focus:border-slate-800"
                />
                <input
                  value={slot.note || ''}
                  onChange={(e) => update(index, 'note', e.target.value)}
                  placeholder="Note, e.g. 10 mins each"
                  aria-label={`Sub-slot ${index + 1} note`}
                  className="w-full bg-white border border-slate-200 rounded-md px-2 py-1 text-xs outline-none focus:border-slate-800"
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Create / edit drawer ─────────────────────────────────────────────────────
function Modal({ item, onSave, onClose }) {
  const [form, setForm] = useState({ ...EMPTY_FORM, ...(item || {}), agenda: agendaOf(item || {}) });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSave = async () => {
    if (!form.title.trim()) { setError('Event title is required.'); return; }
    // An end before the start is only an error when the block does not say it
    // runs past midnight — "7:30 PM to 12:30 AM" is a legitimate offsite slot.
    const start = toMinutes(form.startTime);
    const end = toMinutes(form.endTime);
    if (!form.openEnded && start !== null && end !== null && end <= start && !form.endsNextDay) {
      setError('End time is before the start. Tick "Runs past midnight" if that is intended.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({
        ...form,
        // An open-ended block has no end time to store, and cannot also wrap.
        endTime: form.openEnded ? '' : form.endTime,
        endsNextDay: form.openEnded ? false : Boolean(form.endsNextDay),
        agenda: (form.agenda || []).filter((slot) => (slot.title || '').trim() || slot.startTime),
      });
    } catch {
      setError('Failed to save. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="tw-drawer-backdrop">
      <div className="tw-drawer-panel">
        {/* Header */}
        <div className="p-6 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{item ? 'Edit Itinerary Block' : 'Add Itinerary Block'}</h3>
            <p className="text-xs text-slate-500 mt-1">Times, venue and directions, on-ground POC, and the sub-agenda</p>
          </div>
          <button className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Block Title *</label>
            <input
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder="e.g. Kickoff Corporate Lunch Buffet"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Day Label</label>
              <input
                value={form.day}
                onChange={(e) => set('day', e.target.value)}
                placeholder="Day 1"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Calendar Date</label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => set('date', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Day Theme</label>
            <input
              value={form.dayTheme || ''}
              onChange={(e) => set('dayTheme', e.target.value)}
              placeholder="e.g. Icebreakers & High-Energy Party"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
            />
            <p className="text-[10px] text-slate-400">Shown once under the day heading, not on the block itself.</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Start Time</label>
              <input
                type="time"
                value={form.startTime}
                onChange={(e) => set('startTime', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">End Time</label>
              <input
                type="time"
                value={form.endTime}
                disabled={Boolean(form.openEnded)}
                onChange={(e) => set('endTime', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          {/* Late-night handling — the offsite has two blocks that run past midnight */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex items-start gap-2.5 bg-slate-50 border border-slate-100 p-3 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={Boolean(form.openEnded)}
                onChange={(e) => set('openEnded', e.target.checked)}
                className="w-4 h-4 mt-0.5 rounded text-slate-800 border-slate-300 focus:ring-slate-800 cursor-pointer shrink-0"
              />
              <span className="min-w-0">
                <span className="text-sm font-medium text-slate-700 block">Open ended</span>
                <span className="text-[11px] text-slate-500">Shows as &ldquo;till late&rdquo; with no finish time.</span>
              </span>
            </label>
            <label className={`flex items-start gap-2.5 bg-slate-50 border border-slate-100 p-3 rounded-lg cursor-pointer ${form.openEnded ? 'opacity-50 pointer-events-none' : ''}`}>
              <input
                type="checkbox"
                checked={Boolean(form.endsNextDay)}
                disabled={Boolean(form.openEnded)}
                onChange={(e) => set('endsNextDay', e.target.checked)}
                className="w-4 h-4 mt-0.5 rounded text-slate-800 border-slate-300 focus:ring-slate-800 cursor-pointer shrink-0"
              />
              <span className="min-w-0">
                <span className="text-sm font-medium text-slate-700 block">Runs past midnight</span>
                <span className="text-[11px] text-slate-500">End time falls on the next calendar day.</span>
              </span>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Category</label>
              <select
                value={form.type}
                onChange={(e) => set('type', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
              >
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Venue Name</label>
              <input
                value={form.location}
                onChange={(e) => set('location', e.target.value)}
                placeholder="e.g. Chutney Chang"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Street Address</label>
            <textarea
              value={form.address || ''}
              onChange={(e) => set('address', e.target.value)}
              rows={2}
              placeholder="31, HM Eleganza, Museum Road, near Church Street, Bengaluru"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition resize-none"
            />
            <p className="text-[10px] text-slate-400">Drives the Directions button. Leave the map link below blank to search the venue and address on Google Maps.</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Conference Room</label>
              <input
                value={form.conferenceRoom || ''}
                onChange={(e) => set('conferenceRoom', e.target.value)}
                placeholder="In-house Conference Room"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Map Link (optional)</label>
              <input
                value={form.mapsUrl || ''}
                onChange={(e) => set('mapsUrl', e.target.value)}
                placeholder="https://maps.app.goo.gl/…"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider font-display">Speakers / Presenters</label>
            <input
              value={form.speaker}
              onChange={(e) => set('speaker', e.target.value)}
              placeholder="e.g. Venkat, Srini"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">On-Ground POC Name</label>
              <input
                value={form.pocName || ''}
                onChange={(e) => set('pocName', e.target.value)}
                placeholder="Jane Cooper"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">POC Contact Phone</label>
              <input
                value={form.pocPhone || ''}
                onChange={(e) => set('pocPhone', e.target.value)}
                placeholder="+91-XXXXX-XXXXX"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
              />
            </div>
          </div>

          <div className="border-t border-slate-100 pt-4">
            <AgendaEditor slots={form.agenda || []} onChange={(next) => set('agenda', next)} />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Description (visible to everyone)</label>
            <textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              rows={2}
              placeholder="Directions, dress code, or pre-requisites…"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition resize-none"
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-amber-700 uppercase tracking-wider flex items-center gap-1.5">
              <AlertTriangle size={13} /> Internal Note (admins only)
            </label>
            <textarea
              value={form.internalNote || ''}
              onChange={(e) => set('internalNote', e.target.value)}
              rows={2}
              placeholder="Open questions, unresolved timings, things to chase…"
              className="w-full bg-amber-50/60 border border-amber-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-amber-500 outline-none transition resize-none"
            />
            <p className="text-[10px] text-slate-400">Never shown to employees, published or not.</p>
          </div>

          <div className="flex items-center gap-3 bg-slate-50 border border-slate-100 p-3 rounded-lg">
            <input
              type="checkbox"
              id="published"
              checked={Boolean(form.published)}
              onChange={(e) => set('published', e.target.checked)}
              className="w-4 h-4 rounded text-slate-800 border-slate-300 focus:ring-slate-800 cursor-pointer"
            />
            <label htmlFor="published" className="text-sm font-medium text-slate-700 cursor-pointer select-none">
              Publish to the employee itinerary
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-100 bg-white">
          {error && <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
          <div className="flex gap-3 justify-end">
            <button className="px-4 py-2 text-sm font-semibold border border-slate-200 hover:bg-slate-50 rounded-lg transition" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="px-4 py-2 text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition flex items-center gap-2 disabled:opacity-60" onClick={handleSave} disabled={saving}>
              {saving && <Loader2 size={14} className="animate-spin" />}
              {item ? 'Save Block' : 'Add to Itinerary'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── One block on the timeline ────────────────────────────────────────────────
function ItineraryBlock({ item, status, isNext, readOnly, onEdit, onDelete, onTogglePublish }) {
  const slots = agendaOf(item);
  const [open, setOpen] = useState(false);
  const directions = mapsHref(item);
  const live = status === 'live';
  const past = status === 'past';

  const dotClass = live
    ? 'bg-emerald-500 ring-emerald-100 animate-pulse'
    : past
      ? 'bg-slate-300 ring-white'
      : 'bg-slate-900 ring-white';

  return (
    <div className={`flex gap-3 sm:gap-4 ${past ? 'opacity-60' : ''}`}>
      {/* Time gutter — desktop only; the phone layout repeats it inside the card */}
      <div className="hidden sm:flex flex-col items-end w-[5.5rem] shrink-0 pt-4">
        <span className={`text-sm font-bold font-mono ${live ? 'text-emerald-600' : 'text-slate-900'}`}>
          {item.startTime ? formatTime(item.startTime) : '--:--'}
        </span>
        {durationLabel(item) && (
          <span className="text-[11px] text-slate-400 font-medium">{durationLabel(item)}</span>
        )}
      </div>

      {/* Rail */}
      <div className="relative flex flex-col items-center shrink-0">
        <span className={`mt-5 w-3 h-3 rounded-full ring-4 z-10 shrink-0 ${dotClass}`} />
        <span className="flex-1 w-[2px] bg-slate-150 -mt-1" />
      </div>

      {/* Card */}
      <div className={`flex-1 min-w-0 mb-4 bg-white border rounded-xl p-4 sm:p-5 shadow-sm transition ${live ? 'border-emerald-300 ring-1 ring-emerald-100' : 'border-slate-200 hover:border-slate-300'}`}>
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
          <div className="space-y-3 flex-1 min-w-0">
            {/* Title row */}
            <div className="flex flex-wrap items-center gap-2">
              <h4 className="text-base font-bold text-slate-900 tracking-tight">{item.title}</h4>
              <span className={`sh-badge ${typeColor[item.type] || 'sh-badge-outline'}`}>{item.type}</span>
              {live && (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white bg-emerald-600 rounded-full px-2 py-0.5">
                  <Radio size={10} /> Happening now
                </span>
              )}
              {isNext && !live && (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5">
                  Up next
                </span>
              )}
              {!readOnly && (
                <button
                  onClick={onTogglePublish}
                  title="Click to toggle publish instantly"
                  className={`sh-badge hover:opacity-80 transition font-bold ${item.published ? 'sh-badge-success' : 'sh-badge-outline'}`}
                >
                  {item.published ? 'published' : 'draft'}
                </button>
              )}
            </div>

            {/* Time + venue */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-slate-500 font-medium">
              <span className="sm:hidden inline-flex items-center gap-1 text-slate-700 font-bold bg-slate-50 px-2 py-0.5 rounded border border-slate-100">
                <Clock size={11} className="text-slate-400" />
                {timeRangeLabel(item)}
              </span>
              <span className="hidden sm:inline-flex items-center gap-1 text-slate-600">
                <Clock size={12} className="text-slate-400" />
                {timeRangeLabel(item)}
              </span>
              {(item.location || item.conferenceRoom) && (
                <span className="flex items-center gap-1 min-w-0">
                  <MapPin size={12} className="text-slate-400 shrink-0" />
                  <span className="truncate">{item.location || 'Venue TBD'}</span>
                  {item.conferenceRoom && <strong className="text-slate-800 shrink-0">({item.conferenceRoom})</strong>}
                </span>
              )}
              {item.speaker && (
                <span className="bg-slate-50 border border-slate-100 text-slate-600 px-2 py-0.5 rounded">
                  🎙️ {item.speaker}
                </span>
              )}
            </div>

            {/* Address + directions */}
            {(item.address || directions) && (
              <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
                {item.address && (
                  <p className="text-xs text-slate-500 leading-relaxed flex-1 min-w-[12rem]">{item.address}</p>
                )}
                {directions && (
                  <a
                    href={directions}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-slate-200 text-slate-700 hover:text-slate-950 hover:border-slate-400 rounded-lg text-xs font-semibold shadow-sm transition shrink-0"
                  >
                    <Navigation size={11} />
                    <span>Directions</span>
                  </a>
                )}
              </div>
            )}

            {/* Description */}
            {item.description && (
              <p className="text-sm text-slate-600 leading-relaxed bg-slate-50/50 p-2.5 rounded-lg border border-slate-100">
                {item.description}
              </p>
            )}

            {/* Admin-only internal note */}
            {!readOnly && item.internalNote && (
              <div className="flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2">
                <AlertTriangle size={13} className="text-amber-500 shrink-0 mt-0.5" />
                <span className="whitespace-pre-wrap break-words">
                  <strong className="font-bold">Internal:</strong> {item.internalNote}
                </span>
              </div>
            )}

            {/* Sub-agenda */}
            {slots.length > 0 && (
              <div className="border border-slate-150 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpen((o) => !o)}
                  aria-expanded={open}
                  className="w-full flex items-center gap-2 px-3 py-2 bg-slate-50/70 hover:bg-slate-100/70 transition text-left"
                >
                  <ChevronDown size={14} className={`text-slate-400 transition-transform shrink-0 ${open ? '' : '-rotate-90'}`} />
                  <ListTree size={13} className="text-slate-400 shrink-0" />
                  <span className="text-xs font-bold text-slate-700">
                    {slots.length} session{slots.length > 1 ? 's' : ''} inside this block
                  </span>
                </button>
                {open && (
                  <ol className="divide-y divide-slate-100">
                    {slots.map((slot) => (
                      <li key={slot.id} className="flex items-start gap-3 px-3 py-2">
                        <span className="font-mono text-[11px] font-bold text-slate-500 shrink-0 w-[8.5rem] pt-0.5">
                          {slot.startTime ? formatTime(slot.startTime) : '--:--'}
                          {slot.endTime ? ` – ${formatTime(slot.endTime)}` : ''}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="text-sm text-slate-800 font-medium block">{slot.title}</span>
                          {(slot.owner || slot.note) && (
                            <span className="text-[11px] text-slate-500 block mt-0.5">
                              {[slot.owner, slot.note].filter(Boolean).join(' · ')}
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            )}

            {/* POC */}
            {item.pocName && (
              <div className="flex flex-wrap items-center gap-3 bg-slate-50 border border-slate-150 rounded-lg p-2.5 w-max max-w-full">
                <span className="text-xs text-slate-500">On-ground coordinator:</span>
                <strong className="text-xs text-slate-800">{item.pocName}</strong>
                {item.pocPhone && (
                  <a
                    href={`tel:${item.pocPhone}`}
                    className="inline-flex items-center gap-1.5 px-2 py-1 bg-white border border-slate-200 text-slate-700 hover:text-slate-950 hover:border-slate-400 rounded-md text-xs font-semibold shadow-sm transition"
                  >
                    <PhoneCall size={11} />
                    <span>Call</span>
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Admin actions */}
          {!readOnly && (
            <div className="flex md:flex-col items-center justify-end gap-1.5 border-t md:border-t-0 border-slate-100 pt-3 md:pt-0 shrink-0">
              <button
                className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-50 border border-transparent hover:border-slate-200 rounded-lg transition"
                onClick={onEdit}
                title="Edit block"
                aria-label={`Edit ${item.title}`}
              >
                <Edit2 size={14} />
              </button>
              <button
                className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 border border-transparent hover:border-red-100 rounded-lg transition"
                onClick={onDelete}
                title="Remove block"
                aria-label={`Remove ${item.title}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function Schedule({ readOnly = false }) {
  const { data: items, loading } = useStore('schedule');
  const [modal, setModal] = useState(null);
  const [query, setQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [activeDay, setActiveDay] = useState('all');
  const [importing, setImporting] = useState(false);
  const [now, setNow] = useState(() => new Date());

  // Re-evaluate the now/next markers on a timer rather than only on render.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), CLOCK_TICK_MS);
    return () => clearInterval(id);
  }, []);

  const visibleItems = useMemo(
    () => sortItems(readOnly ? items.filter((item) => item.published) : items),
    [items, readOnly]
  );

  // Distinct days, in order, for the tab strip.
  const days = useMemo(
    () => groupByDay(visibleItems).map(({ date, day, theme, items: dayItems }) => ({
      date, day, theme, count: dayItems.length,
    })),
    [visibleItems]
  );

  // Land on today when the offsite is running, otherwise show everything.
  const didAutoSelectDay = useRef(false);
  useEffect(() => {
    if (didAutoSelectDay.current || days.length === 0) return;
    didAutoSelectDay.current = true;
    const today = toISODate(new Date());
    if (days.some((d) => d.date === today)) setActiveDay(today);
  }, [days]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return visibleItems.filter((item) => {
      if (activeDay !== 'all' && (item.date || '') !== activeDay) return false;
      if (selectedType !== 'all' && item.type !== selectedType) return false;
      if (!q) return true;
      // Search covers the sub-agenda too, so a slot like "Product strategy"
      // still surfaces the block that contains it.
      const haystack = [
        item.title, item.location, item.address, item.conferenceRoom,
        item.speaker, item.description, item.day,
        ...agendaOf(item).flatMap((slot) => [slot.title, slot.owner, slot.note]),
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [visibleItems, activeDay, selectedType, query]);

  const grouped = useMemo(() => groupByDay(filtered), [filtered]);
  const nextId = useMemo(() => nextUpId(visibleItems, now), [visibleItems, now]);

  const save = async (form) => {
    await put('schedule', { ...form, id: form.id || generateId() });
    setModal(null);
    window.dispatchEvent(new CustomEvent('planner:data-change'));
    toast.success(form.id ? 'Block updated.' : 'Block added to the itinerary.');
  };

  const togglePublish = async (item) => {
    await put('schedule', { ...item, published: !item.published });
    window.dispatchEvent(new CustomEvent('planner:data-change'));
    toast.info(item.published ? 'Block unpublished.' : 'Block published.');
  };

  const del = async (item) => {
    const ok = await confirm(`Remove "${item.title}" from the itinerary?`, 'Delete Block');
    if (!ok) return;
    try {
      await remove('schedule', item.id);
      window.dispatchEvent(new CustomEvent('planner:data-change'));
      toast.success('Block removed.');
    } catch {
      toast.error('Failed to delete the block.');
    }
  };

  // Replace the whole itinerary with the packaged offsite agenda. Destructive
  // by design — the alternative (adding alongside) silently duplicates the
  // agenda every time someone re-runs it.
  const importOffsite = async () => {
    if (importing) return;
    const existing = items.length;
    const ok = await confirm(
      `Replace the itinerary with the ${OFFSITE_BLOCK_COUNT}-block Nativeorange offsite agenda ` +
      `(${OFFSITE_SLOT_COUNT} sub-sessions across Fri 4 and Sat 5 September 2026)?\n\n` +
      (existing
        ? `This permanently DELETES the ${existing} block${existing > 1 ? 's' : ''} currently in the itinerary.`
        : 'The itinerary is currently empty.') +
      '\n\nAll imported blocks are published to employees immediately.',
      'Import Offsite Agenda'
    );
    if (!ok) return;

    setImporting(true);
    try {
      // Clear first so blocks removed from the seed do not linger, then write
      // the agenda. Fixed seed ids keep a repeat import idempotent.
      for (const row of items) {
        await remove('schedule', row.id);
      }
      for (const row of OFFSITE_ITINERARY) {
        await put('schedule', row);
      }
      window.dispatchEvent(new CustomEvent('planner:data-change'));
      setActiveDay('all');
      toast.success(`Imported ${OFFSITE_BLOCK_COUNT} blocks and ${OFFSITE_SLOT_COUNT} sub-sessions.`);
    } catch (e) {
      toast.error(e.message || 'Import failed — the itinerary may be partly written.');
    } finally {
      setImporting(false);
    }
  };

  if (loading) return <PageLoader label="Loading itinerary…" sublabel="Fetching the offsite schedule" />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold font-display text-slate-900 flex items-center gap-2">
            <Calendar style={{ color: 'var(--accent)' }} size={24} />
            <span>{readOnly ? 'Offsite Itinerary' : 'Itinerary Builder'}</span>
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {readOnly
              ? 'Your day-by-day plan — timings, venues, directions, and who to call on the ground'
              : 'Build the day-wise plan, break long blocks into sub-sessions, and publish it to the team'}
          </p>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <button
              className="px-3 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg text-sm font-semibold transition flex items-center gap-2 shadow-sm disabled:opacity-50"
              onClick={importOffsite}
              disabled={importing}
              title="Replace the itinerary with the packaged offsite agenda"
            >
              {importing ? <Loader2 size={15} className="animate-spin" /> : <DownloadCloud size={15} />}
              <span>{importing ? 'Importing…' : 'Import offsite agenda'}</span>
            </button>
            <button
              className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-semibold transition flex items-center gap-2 shadow-sm"
              onClick={() => setModal('new')}
            >
              <Plus size={16} />
              <span>Add Block</span>
            </button>
          </div>
        )}
      </div>

      {/* Day tabs */}
      {days.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-1 select-none">
          <button
            onClick={() => setActiveDay('all')}
            className={`px-3.5 py-2 rounded-lg text-xs font-bold border transition shrink-0 ${activeDay === 'all' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
          >
            All days ({visibleItems.length})
          </button>
          {days.map((d) => {
            const active = activeDay === d.date;
            const isToday = d.date === toISODate(now);
            return (
              <button
                key={d.date || 'unscheduled'}
                onClick={() => setActiveDay(d.date)}
                className={`px-3.5 py-2 rounded-lg text-xs font-bold border transition shrink-0 flex items-center gap-2 ${active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
              >
                <span>{d.day || (d.date ? shortDateLabel(d.date) : 'Unscheduled')}</span>
                <span className={active ? 'text-slate-300 font-medium' : 'text-slate-400 font-medium'}>
                  {d.day && d.date ? `${shortDateLabel(d.date)} · ` : ''}{d.count}
                </span>
                {isToday && (
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" title="Today" />
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search blocks, sub-sessions, venues, presenters…"
            className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-8 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              title="Clear search"
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md p-1 transition"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:bg-white focus:border-slate-800 outline-none shrink-0 sm:w-56"
        >
          <option value="all">All categories ({visibleItems.length})</option>
          {TYPES.map((t) => (
            <option key={t} value={t} className="capitalize">
              {t} ({visibleItems.filter((i) => i.type === t).length})
            </option>
          ))}
        </select>
      </div>

      {/* Timeline */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
          <Inbox className="mx-auto text-slate-300 mb-3" size={36} />
          <h3 className="text-base font-bold text-slate-800">
            {visibleItems.length === 0 ? 'No itinerary yet' : 'No blocks match'}
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            {visibleItems.length === 0
              ? (readOnly
                ? 'The organisers have not published the itinerary yet.'
                : 'Import the packaged offsite agenda, or add the first block by hand.')
              : 'Try a different day, category, or search term.'}
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {grouped.map((group) => (
            <div key={group.date || 'unscheduled'} className="space-y-3">
              {/* Day heading */}
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 pb-2 border-b border-slate-200">
                <h3 className="text-lg font-bold text-slate-900 tracking-tight font-display">
                  {group.day || longDateLabel(group.date)}
                </h3>
                {group.day && (
                  <span className="text-sm text-slate-500 font-medium">{longDateLabel(group.date)}</span>
                )}
                {group.theme && (
                  <span className="text-xs text-slate-500 italic">· {group.theme}</span>
                )}
                <span className="text-xs text-slate-400 ml-auto">
                  {group.items.length} block{group.items.length > 1 ? 's' : ''}
                </span>
              </div>

              {/* Blocks */}
              <div className="pt-1">
                {group.items.map((item) => (
                  <ItineraryBlock
                    key={item.id}
                    item={item}
                    status={statusOf(item, now)}
                    isNext={item.id === nextId}
                    readOnly={readOnly}
                    onEdit={() => setModal(item)}
                    onDelete={() => del(item)}
                    onTogglePublish={() => togglePublish(item)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && <Modal item={modal === 'new' ? null : modal} onSave={save} onClose={() => setModal(null)} />}
    </div>
  );
}
